import { CheckCircleIcon, InfoIcon, type Icon } from '@phosphor-icons/react';

import { BillingCadence } from 'src/types/enums';

export type SelfBillIneligibilityReason =
  | 'NotExecuted'
  | 'DocumentsNotApproved'
  | 'ReviewFlags'
  | 'SupplierNotEnabled'
  | 'AlreadyInvoiced'
  | 'AlreadyOnSelfBill'
  | 'Excluded';

const REASON_LABEL: Record<SelfBillIneligibilityReason, string> = {
  NotExecuted: 'Order is nog niet uitgevoerd',
  DocumentsNotApproved: 'Documenten zijn niet goedgekeurd',
  // ReviewFlags is surfaced (with actions) by OrderSelfBillReviewBanner — kept
  // out of this note to avoid a duplicate message.
  ReviewFlags: 'Tegengehouden voor review',
  SupplierNotEnabled: 'Vervoerder staat niet ingesteld voor ritoverzicht of self billing',
  AlreadyInvoiced: 'Order heeft al een gekoppelde aankoopfactuur',
  AlreadyOnSelfBill: 'Order staat al op een ritoverzicht of self billing',
  Excluded: 'Order is uitgesloten van ritoverzicht en self billing',
};

/**
 * Whether the order page offers to exclude an order: one on a draft, or an executed order of a carrier on
 * self-billing that isn't billed yet and so can still end up on a run. An issued self-bill keeps its orders.
 */
export function canExcludeFromSelfBilling(reasons: SelfBillIneligibilityReason[], purchaseLocked: boolean): boolean {
  if (purchaseLocked) {
    return false;
  }
  if (reasons.includes('AlreadyOnSelfBill')) {
    return true;
  }
  const neverPickedUp: SelfBillIneligibilityReason[] = ['NotExecuted', 'SupplierNotEnabled', 'AlreadyInvoiced'];
  return !reasons.some((reason) => neverPickedUp.includes(reason));
}

export interface SelfBillEligibilityStatus {
  tone: 'positive' | 'muted';
  icon: Icon;
  /** Short headline, fits on one line. */
  text: string;
  /** Why the order is not picked up; shown in a tooltip and in the expanded details. */
  details?: string[];
}

/**
 * One-line summary, shown in the order documents bar, of whether the order is
 * picked up by self-billing and — when it is not — why. Complements the green
 * documents check, which only reflects document validation, not the full
 * self-billing eligibility. Returns `null` when there is nothing worth saying.
 */
export function getSelfBillEligibilityStatus(
  eligible: boolean,
  reasons: SelfBillIneligibilityReason[],
  billingCadence: BillingCadence | null,
): SelfBillEligibilityStatus | null {
  if (eligible) {
    // Weekly (regular) carriers are only picked up by the Monday run, so say when.
    const text =
      billingCadence === BillingCadence.WEEKLY
        ? 'Komt maandag op een ritoverzicht of self billing'
        : 'Komt in aanmerking voor ritoverzicht of self billing';
    return { tone: 'positive', icon: CheckCircleIcon, text };
  }

  // An order that hasn't been executed yet was never going to end up on a
  // self-bill regardless of its other reasons, so there is nothing to warn about.
  if (reasons.includes('NotExecuted')) {
    return null;
  }

  // Already being on a self-bill is a resolved, benign state.
  if (reasons.includes('AlreadyOnSelfBill')) {
    return { tone: 'positive', icon: CheckCircleIcon, text: REASON_LABEL.AlreadyOnSelfBill };
  }

  // The review-flag reason already has its own actionable banner; if that is the
  // only reason there is nothing extra to explain here.
  const shownReasons = reasons.filter((r) => r !== 'ReviewFlags');
  if (shownReasons.length === 0) {
    return null;
  }

  return {
    tone: 'muted',
    icon: InfoIcon,
    text: 'Niet opgenomen in ritoverzicht of self billing',
    details: shownReasons.map((r) => REASON_LABEL[r]),
  };
}
